import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { teamActivityAPI } from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import LiveActivityCard from "../../../components/schedule/LiveActivityCard";
import WorkloadTimeline from "../../../components/schedule/WorkloadTimeline";
import "../../../components/projects/Projects.css";
export default function TeamActivityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("live");
  const [liveItems, setLiveItems] = useState([]);
  const [workloadItems, setWorkloadItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);

  // Calculate week start and end
  const { weekStart, weekEnd, weekLabel } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + 1); // Monday
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Sunday

    return {
      weekStart: start.toLocaleDateString("en-CA"), // "2026-03-16"
      weekEnd: end.toLocaleDateString("en-CA"), // "2026-03-22"
      weekLabel: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
    };
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch both in parallel to get different date ranges
      const [liveResp, workloadResp] = await Promise.all([
        teamActivityAPI.list(), // today only
        teamActivityAPI.list({ weekStart, weekEnd }), // full week
      ]);
      setLiveItems(liveResp.data || []);
      setWorkloadItems(workloadResp.data || []);
      setLastUpdated(Date.now());
      setSecondsAgo(0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load team activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const refresh = setInterval(load, 60000);
    return () => clearInterval(refresh);
  }, [weekStart, weekEnd]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [lastUpdated]);

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <div>
          <h1>What your team is doing right now</h1>
          <div className="small-muted">
            Updates every 60 seconds · Last updated {secondsAgo}s ago
          </div>
        </div>
        <button className="btn-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="project-actions" style={{ marginBottom: 12 }}>
        <button
          className={tab === "live" ? "btn-primary" : "btn-secondary"}
          onClick={() => setTab("live")}
        >
          Live Activity
        </button>
        <button
          className={tab === "workload" ? "btn-primary" : "btn-secondary"}
          onClick={() => setTab("workload")}
        >
          Workload View
        </button>
      </div>

      {loading && <div className="small-muted">Loading...</div>}
      {error && (
        <div className="small-muted" style={{ color: "#b91c1c" }}>
          {error}
        </div>
      )}

      {tab === "live" && (
        <div className="modal-grid" style={{ gap: 12 }}>
          {liveItems.map((item) => (
            <LiveActivityCard
              key={item.userId}
              item={item}
              onViewProject={(item) => {
                const { projectId, assignmentId } = item.activeAssignment;
                if (item.userId === user?.id && item.status !== "UPCOMING") {
                  navigate(`/projects/${projectId}/stage/${assignmentId}`);
                } else {
                  navigate(`/projects/${projectId}`);
                }
              }}
              onAssign={() => {}}
            />
          ))}
          {!loading && liveItems.length === 0 && (
            <div className="small-muted">No activity yet.</div>
          )}
        </div>
      )}

      {tab === "workload" && (
        <WorkloadTimeline items={workloadItems} weekLabel={weekLabel} />
      )}
    </div>
  );
}
